import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, MapPin } from "lucide-react";
import { getProducts, getLocations } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { LeadForm } from "../components/LeadForm";

const HERO_BG = "https://images.unsplash.com/photo-1725793558251-2723d6e21efe?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const ROCK = "https://images.unsplash.com/photo-1649113103373-ff47816ae721?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const ease = [0.76, 0, 0.24, 1];
const Line = ({ children, delay = 0 }) => (
  <span className="reveal-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const chapters = [
  { no: "01", title: "Visit BeeHive Medical", body: "Meet with a licensed provider and get evaluated for your Utah medical cannabis card — often the same day." },
  { no: "02", title: "Get Your Medical Card", body: "We handle the paperwork and the state registry so you walk out a verified patient, ready to shop." },
  { no: "03", title: "Shop the Pharmacy", body: "Browse a pharmacist-curated menu online, reserve for pickup, and get expert guidance from our budtenders." },
  { no: "04", title: "Repurchase & Renew", body: "Reorder favorites in seconds, earn Beehive VIP rewards, and get automatic card-renewal reminders." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [locations, setLocations] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);

  useEffect(() => {
    getProducts({ featured: true, limit: 8 }).then(setFeatured).catch(() => {});
    getLocations().then(setLocations).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen min-h-[680px] w-full overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={HERO_BG} alt="Utah landscape" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/30" />
        </motion.div>

        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-20">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-xs tracking-[0.3em] uppercase font-bold text-honey mb-6">
            Utah&rsquo;s Pharmacist-Led Cannabis Pharmacy
          </motion.div>
          <h1 className="font-display font-extrabold uppercase text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.88]">
            <Line delay={0.1}>Medicine</Line>
            <Line delay={0.22}>Meets the</Line>
            <Line delay={0.34}><span className="text-honey">Wasatch.</span></Line>
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 mt-10">
            <Link to="/menu" data-testid="hero-shop-cta"
              className="rounded-full bg-honey text-ink px-8 py-4 font-bold tracking-widest uppercase hover:bg-bone hover:scale-105 transition-all duration-300 inline-flex items-center gap-2 justify-center">
              Shop the Menu <ArrowRight size={18} />
            </Link>
            <Link to="/medical-card" data-testid="hero-card-cta"
              className="rounded-none border border-bone/20 px-8 py-4 font-medium tracking-widest uppercase hover:bg-bone hover:text-ink transition-colors duration-300 text-center">
              Get Your Medical Card
            </Link>
          </motion.div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y border-bone/10 bg-ink2">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 divide-x divide-bone/10">
          {[["20%", "Target Conversion"], ["3", "Utah Locations"], ["4.8★", "Patient Rating"], ["1000+", "Verified Products"]].map(([n, l]) => (
            <div key={l} className="py-10 px-6 text-center">
              <div className="font-display font-extrabold text-4xl md:text-5xl text-honey tracking-tighter">{n}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-honey mb-4">Curated by Pharmacists</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight leading-none">Featured Drops</h2>
          </div>
          <Link to="/menu" className="text-sm uppercase tracking-widest text-bone/80 hover:text-honey inline-flex items-center gap-2">
            View Full Menu <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="bg-ink2 border-y border-bone/10 py-24 lg:py-32">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="lg:sticky lg:top-28">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-honey mb-4">The Patient Journey</div>
              <h2 className="font-display font-extrabold uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9] mb-8">
                From First Visit<br />to Lifelong Care
              </h2>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={ROCK} alt="Utah canyon" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="space-y-2">
              {chapters.map((c, i) => (
                <motion.div key={c.no}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.05 }}
                  className="border-t border-bone/10 py-8 grid grid-cols-[auto_1fr] gap-6">
                  <span className="font-display font-extrabold text-3xl text-honey/40">{c.no}</span>
                  <div>
                    <h3 className="font-display font-bold text-2xl mb-2">{c.title}</h3>
                    <p className="text-stone leading-relaxed">{c.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LOCATIONS */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-honey mb-4">Find Us</div>
        <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight leading-none mb-14">Three Utah Homes</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {locations.map((l) => (
            <Link key={l.id} to={`/locations/${l.id}`} data-testid={`location-${l.id}`}
              className="group bg-ink2 border border-bone/5 hover:border-honey/50 p-8 transition-colors duration-500">
              <MapPin className="text-honey mb-6" size={28} />
              <h3 className="font-display font-bold text-2xl mb-3 group-hover:text-honey transition-colors">{l.city}</h3>
              <p className="text-stone text-sm mb-2">{l.address}</p>
              <p className="text-stone text-sm">{l.hours}</p>
              <span className="inline-flex items-center gap-2 text-honey text-xs uppercase tracking-widest mt-6">
                View Local Page <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* VIP / REVIEW */}
      <section className="bg-honey text-ink py-24 lg:py-32">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold mb-4">Beehive VIP</div>
            <h2 className="font-display font-extrabold uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9] mb-6">
              Join The Hive.<br />Save 25%.
            </h2>
            <p className="text-ink/80 text-lg max-w-md mb-8">
              New patients relocating with us get 25% off for six months. Members earn rewards on every visit and get first access to new drops.
            </p>
            <div className="flex items-center gap-3 mb-8">
              {[...Array(5)].map((_, i) => <Star key={i} size={20} className="fill-ink text-ink" />)}
              <span className="text-sm font-medium">Leave us a Google review & earn bonus points</span>
            </div>
          </div>
          <div className="bg-ink text-bone p-8 lg:p-12">
            <h3 className="font-display font-bold text-2xl mb-6">Become a Member</h3>
            <LeadForm kind="vip" cta="Join Beehive VIP" />
          </div>
        </div>
      </section>
    </main>
  );
}
